/**
 * Archive Service (Web)
 *
 * Owns the employee archive workflow at
 * /companies/{companyId}/archivedEmployees/{userId} and
 * /companies/{companyId}/pendingArchives/{userId}.
 *
 * Extracted from src/firebase.js per the service-decomposition plan
 * (docs/architecture/scalability-architecture.md §4). firebase.js retains
 * temporary re-export shims marked `// TODO remove shim — refactor:archiveService`.
 *
 * Public API:
 *   startArchiveEmployee(userId, companyId)
 *   completeArchiveEmployee(userId, companyId)
 *   getPendingArchiveEmployees(companyId)
 *   cancelArchiveEmployee(userId, companyId)
 *   archiveEmployeeData(userId, companyId)
 *   unarchiveEmployee(userId, companyId)
 *   getAllArchivedEmployees(companyId)
 *   getArchivedEmployeeData(userId, companyId)
 *
 * Cross-cutting rules:
 *   - Tenant scoping enforced by Firebase rules; this service trusts auth context.
 *   - completeArchiveEmployee verifies all time records are approved before archiving.
 *   - archiveEmployeeData marks the user as archived (archived: true) but does NOT delete the user node.
 *   - NOTE: USER_ARCHIVED event is not yet emitted here — that gap is tracked in .claude/tasks.md
 *     under "timeRecordService emits zero events" (same pattern). Flag for eventService wiring.
 */

import { ref, set, get, push, update, remove } from 'firebase/database';
import { getFirebase } from '../firebase';

// Dev-only logger — no-op in production builds.
const dbg = process.env.NODE_ENV !== 'production' ? console.log.bind(console) : () => {};

// ─── Archive Workflow ──────────────────────────────────────────────────────────

/**
 * Step 1: Mark employee for pending archive (doesn't move data yet).
 *
 * @param {string} userId
 * @param {string|null} companyId
 * @returns {Promise<{status: string, employee: object}>}
 */
export const startArchiveEmployee = async (userId, companyId = null) => {
  try {
    const { database } = getFirebase();
    const archiveTimestamp = new Date().toISOString();

    // Define paths
    const userPath = companyId && companyId !== 'legacy'
      ? `companies/${companyId}/users/${userId}`
      : `users/${userId}`;

    const pendingArchivePath = companyId && companyId !== 'legacy'
      ? `companies/${companyId}/pendingArchives/${userId}`
      : `pendingArchives/${userId}`;

    // 1. Get user data
    const userRef = ref(database, userPath);
    const userSnapshot = await get(userRef);

    if (!userSnapshot.exists()) {
      throw new Error('User not found');
    }

    const userData = userSnapshot.val();

    // 2. Mark user as pending archive
    await update(userRef, {
      archiveStatus: 'pending',
      archivePendingAt: archiveTimestamp
    });

    // 3. Create pending archive entry
    const pendingRef = ref(database, pendingArchivePath);
    await set(pendingRef, {
      ...userData,
      archiveStatus: 'pending',
      archivePendingAt: archiveTimestamp,
      initiatedBy: 'Employer'
    });

    dbg(`Employee ${userData.name} marked for pending archive`);
    return { status: 'pending', employee: userData };
  } catch (error) {
    console.error('Error starting archive process:', error);
    throw error;
  }
};

/**
 * Step 2: Complete archive after all records are approved.
 *
 * @param {string} userId
 * @param {string|null} companyId
 * @returns {Promise<object>} archiveData returned by archiveEmployeeData
 */
export const completeArchiveEmployee = async (userId, companyId = null) => {
  try {
    const { database } = getFirebase();
    const archiveTimestamp = new Date().toISOString();

    // Define paths
    const userPath = companyId && companyId !== 'legacy'
      ? `companies/${companyId}/users/${userId}`
      : `users/${userId}`;

    const archivePath = companyId && companyId !== 'legacy'
      ? `companies/${companyId}/archivedEmployees/${userId}`
      : `archivedEmployees/${userId}`;

    const pendingArchivePath = companyId && companyId !== 'legacy'
      ? `companies/${companyId}/pendingArchives/${userId}`
      : `pendingArchives/${userId}`;

    const timeRecordsPath = companyId && companyId !== 'legacy'
      ? `companies/${companyId}/timeRecords`
      : 'timeRecords';

    // 1. Verify all records are approved
    const recordsRef = ref(database, timeRecordsPath);
    const recordsSnapshot = await get(recordsRef);
    const allRecords = recordsSnapshot.val() || {};

    let hasUnapproved = false;
    Object.values(allRecords).forEach(record => {
      // Check if record belongs to this employee and is not approved
      // A record is approved if ANY of these are true: approved, recruiterApproved, employerApproved
      if (record.employeeId === userId) {
        const isApproved = record.approved === true ||
                          record.recruiterApproved === true ||
                          record.employerApproved === true;
        if (!isApproved) {
          hasUnapproved = true;
        }
      }
    });

    if (hasUnapproved) {
      throw new Error('Cannot complete archive: Some time records are not approved');
    }

    // 2. Get user data
    const userRef = ref(database, userPath);
    const userSnapshot = await get(userRef);
    const userData = userSnapshot.val();

    // 3. Perform the actual archive (same as before)
    const archiveData = await archiveEmployeeData(userId, companyId);

    // 4. Remove from pending archives
    const pendingRef = ref(database, pendingArchivePath);
    await remove(pendingRef);

    dbg(`Employee ${userData.name} archive completed successfully`);
    return archiveData;
  } catch (error) {
    console.error('Error completing archive:', error);
    throw error;
  }
};

/**
 * Get all employees pending archive for a company.
 *
 * @param {string|null} companyId
 * @returns {Promise<object>} map of userId → pending archive record, or {}
 */
export const getPendingArchiveEmployees = async (companyId = null) => {
  try {
    const { database } = getFirebase();

    const pendingPath = companyId && companyId !== 'legacy'
      ? `companies/${companyId}/pendingArchives`
      : `pendingArchives`;

    const pendingRef = ref(database, pendingPath);
    const snapshot = await get(pendingRef);

    if (!snapshot.exists()) {
      return {};
    }

    return snapshot.val();
  } catch (error) {
    console.error('Error getting pending archive employees:', error);
    throw error;
  }
};

/**
 * Cancel a pending archive — removes the pending entry and clears archive status on the user.
 *
 * @param {string} userId
 * @param {string|null} companyId
 * @returns {Promise<true>}
 */
export const cancelArchiveEmployee = async (userId, companyId = null) => {
  try {
    const { database } = getFirebase();

    const userPath = companyId && companyId !== 'legacy'
      ? `companies/${companyId}/users/${userId}`
      : `users/${userId}`;

    const pendingArchivePath = companyId && companyId !== 'legacy'
      ? `companies/${companyId}/pendingArchives/${userId}`
      : `pendingArchives/${userId}`;

    // Remove archive status from user
    const userRef = ref(database, userPath);
    await update(userRef, {
      archiveStatus: null,
      archivePendingAt: null
    });

    // Remove from pending archives
    const pendingRef = ref(database, pendingArchivePath);
    await remove(pendingRef);

    dbg(`Archive cancelled for employee ${userId}`);
    return true;
  } catch (error) {
    console.error('Error cancelling archive:', error);
    throw error;
  }
};

/**
 * Comprehensive archive function with detailed statistics and calculations.
 * Copies employee + time records to archivedEmployees path and marks the user node
 * as archived (does NOT delete the user node).
 *
 * @param {string} userId
 * @param {string|null} companyId
 * @returns {Promise<object>} archiveData written to the archive path
 */
export const archiveEmployeeData = async (userId, companyId = null) => {
  try {
    const { database } = getFirebase();
    const archiveTimestamp = new Date().toISOString();

    // Define paths
    const userPath = companyId && companyId !== 'legacy'
      ? `companies/${companyId}/users/${userId}`
      : `users/${userId}`;

    const archivePath = companyId && companyId !== 'legacy'
      ? `companies/${companyId}/archivedEmployees/${userId}`
      : `archivedEmployees/${userId}`;

    const timeRecordsPath = companyId && companyId !== 'legacy'
      ? `companies/${companyId}/timeRecords`
      : 'timeRecords';

    // 1. Get user data
    const userRef = ref(database, userPath);
    const userSnapshot = await get(userRef);

    if (!userSnapshot.exists()) {
      throw new Error('User not found');
    }

    const userData = userSnapshot.val();

    // 2. Get all time records for this employee
    const recordsRef = ref(database, timeRecordsPath);
    const recordsSnapshot = await get(recordsRef);
    const allRecords = recordsSnapshot.val() || {};

    // Filter records for this employee and calculate hours
    const employeeRecords = {};
    let totalApprovedMinutes = 0;
    let totalPendingMinutes = 0;
    let totalRecords = 0;
    let approvedRecords = 0;
    let pendingRecords = 0;

    Object.entries(allRecords).forEach(([recordId, record]) => {
      if (record.employeeId === userId || record.employeeId === userData.id) {
        employeeRecords[recordId] = record;
        totalRecords++;

        // Calculate hours from various possible fields
        let minutesWorked = 0;
        const hoursField = record.hoursWorked || record.totalHours || record.hours || 0;

        if (typeof hoursField === 'string') {
          if (hoursField.includes(':')) {
            // Format: "8:30"
            const [hours, minutes] = hoursField.split(':').map(Number);
            minutesWorked = (hours || 0) * 60 + (minutes || 0);
          } else {
            // Format: "8.5"
            minutesWorked = parseFloat(hoursField) * 60;
          }
        } else if (typeof hoursField === 'number') {
          minutesWorked = hoursField * 60;
        }

        // Add to appropriate total based on approval status
        if (record.approved === true) {
          totalApprovedMinutes += minutesWorked;
          approvedRecords++;
        } else if (record.approved === false || record.approved === null || record.approved === undefined) {
          totalPendingMinutes += minutesWorked;
          pendingRecords++;
        }
      }
    });

    // Convert total minutes to hours with proper rounding
    const totalApprovedHours = Math.round(totalApprovedMinutes) / 60;
    const totalPendingHours = Math.round(totalPendingMinutes) / 60;

    // 3. Create archive data structure
    const archiveData = {
      employee: {
        ...userData,
        archivedAt: archiveTimestamp,
        archivedBy: 'Employer'
      },
      timeRecords: employeeRecords,
      summary: {
        totalApprovedHours: totalApprovedHours.toFixed(2),
        totalApprovedMinutes: Math.round(totalApprovedMinutes),
        totalPendingHours: totalPendingHours.toFixed(2),
        totalPendingMinutes: Math.round(totalPendingMinutes),
        totalRecords: totalRecords,
        approvedRecords: approvedRecords,
        pendingRecords: pendingRecords,
        yearToDate: new Date().getFullYear(),
        archivedAt: archiveTimestamp
      }
    };

    // 4. Save to archive location
    const archiveRef = ref(database, archivePath);
    await set(archiveRef, archiveData);

    // 5. Mark user as archived (don't delete, just mark)
    await update(userRef, {
      archived: true,
      archivedAt: archiveTimestamp,
      archiveLocation: archivePath
    });

    dbg(`Employee ${userData.name} archived successfully with ${Object.keys(employeeRecords).length} time records`);
    dbg(`Total approved hours: ${totalApprovedHours.toFixed(2)}`);

    return archiveData;
  } catch (error) {
    console.error('Error archiving employee:', error);
    throw error;
  }
};

/**
 * Unarchive an employee — marks the user node as not archived. Does NOT move
 * records back from archivedEmployees (those remain for audit purposes).
 *
 * @param {string} userId
 * @param {string|null} companyId
 * @returns {Promise<true>}
 */
export const unarchiveEmployee = async (userId, companyId = null) => {
  try {
    const { database } = getFirebase();

    const userPath = companyId && companyId !== 'legacy'
      ? `companies/${companyId}/users/${userId}`
      : `users/${userId}`;

    const userRef = ref(database, userPath);

    // Simply mark as not archived
    await update(userRef, {
      archived: false,
      archivedAt: null,
      unarchivedAt: new Date().toISOString()
    });

    dbg(`Employee ${userId} unarchived successfully`);
    return true;
  } catch (error) {
    console.error('Error unarchiving employee:', error);
    throw error;
  }
};

/**
 * Get all archived employees for a company.
 *
 * @param {string|null} companyId
 * @returns {Promise<object>} map of userId → archive record, or {}
 */
export const getAllArchivedEmployees = async (companyId = null) => {
  try {
    const { database } = getFirebase();

    const archivePath = companyId && companyId !== 'legacy'
      ? `companies/${companyId}/archivedEmployees`
      : `archivedEmployees`;

    const archiveRef = ref(database, archivePath);
    const snapshot = await get(archiveRef);

    if (!snapshot.exists()) {
      return {};
    }

    return snapshot.val();
  } catch (error) {
    console.error('Error getting archived employees:', error);
    throw error;
  }
};

/**
 * Retrieve a single archived employee's data (employee + timeRecords + summary).
 *
 * @param {string} userId
 * @param {string|null} companyId
 * @returns {Promise<object|null>} archive record, or null if not found
 */
export const getArchivedEmployeeData = async (userId, companyId = null) => {
  try {
    const { database } = getFirebase();

    const archivePath = companyId && companyId !== 'legacy'
      ? `companies/${companyId}/archivedEmployees/${userId}`
      : `archivedEmployees/${userId}`;

    const archiveRef = ref(database, archivePath);
    const snapshot = await get(archiveRef);

    if (!snapshot.exists()) {
      return null;
    }

    return snapshot.val();
  } catch (error) {
    console.error('Error retrieving archived employee data:', error);
    throw error;
  }
};
